import React, { useState } from "react";
import PropTypes from "prop-types";
import { Breadcrumbs, Typography, Link, Tooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WorkAreaAccordion from "./WorkAreaAccordion";
import { roundToTwoDecimals } from "../../../../utils/formHelper";
import "./UnderPaymentDetail.scss";

const formatCurrency = value =>
  `$${Number(roundToTwoDecimals(Number(value ?? 0))).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

export default function UnderPaymentDetail({ record, onBackToList }) {
  const {
    employeeName,
    weekEnding,
    project,
    laborClassification,
    grossAmount,
    totalBackPayment,
    workAreas
  } = record ?? {};

  const [expandedIds, setExpandedIds] = useState(workAreas?.length ? [workAreas[0].id] : []);

  const allExpanded = workAreas.length > 0 && expandedIds.length === workAreas.length;
  const hasWorkAreaNames = workAreas.some(workArea => workArea.workAreaName);

  const handleToggleAccordion = id => {
    setExpandedIds(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id]
    );
  };

  const handleExpandAllToggle = () => {
    setExpandedIds(allExpanded ? [] : workAreas.map(workArea => workArea.id));
  };

  return (
    <div className="UnderPaymentDetail">
      <Breadcrumbs className="UnderPaymentDetail-breadcrumbs" separator="/">
        <Link
          component="button"
          type="button"
          className="UnderPaymentDetail-breadcrumbLink"
          onClick={onBackToList}
        >
          Prevailing Wage Underpayments
        </Link>
        <Typography className="UnderPaymentDetail-breadcrumbCurrent">
          {employeeName} &middot; week ending {weekEnding}
        </Typography>
      </Breadcrumbs>

      <Typography className="UnderPaymentDetail-title">
        {employeeName} was paid less than prevailing wage for the week ending {weekEnding} on {project}.
      </Typography>

      <div className="UnderPaymentDetail-summary">
        <div className="UnderPaymentDetail-summaryItem">
          <span className="UnderPaymentDetail-summaryLabel">Project</span>
          <span className="UnderPaymentDetail-summaryValue">{project}</span>
        </div>

        <div className="UnderPaymentDetail-summaryItem">
          <span className="UnderPaymentDetail-summaryLabel">Week Ending</span>
          <span className="UnderPaymentDetail-summaryValue">{weekEnding}</span>
        </div>

        <div className="UnderPaymentDetail-summaryItem">
          <span className="UnderPaymentDetail-summaryLabel">Labor Classification</span>
          <span className="UnderPaymentDetail-summaryValue">{laborClassification}</span>
        </div>

        <div className="UnderPaymentDetail-summaryItem">
          <span className="UnderPaymentDetail-summaryLabel">Gross Amount (Project)</span>
          <span className="UnderPaymentDetail-summaryValue">{formatCurrency(grossAmount)}</span>
        </div>

        <div className="UnderPaymentDetail-summaryItem">
          <span className="UnderPaymentDetail-summaryLabel">
            Total Back Payment (Interest Not Included)
            <Tooltip title="For more information, please contact your CSM or your tax advisor for additional support.">
              <IconButton size="small" className="UnderPaymentDetail-infoIcon">
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </span>
          <span className="UnderPaymentDetail-summaryValue UnderPaymentDetail-summaryValue--alert">
            {formatCurrency(totalBackPayment)}
          </span>
        </div>
      </div>

      <div className="UnderPaymentDetail-workAreasHeader">
        <Typography className="UnderPaymentDetail-workAreasCount">
          {workAreas.length} {hasWorkAreaNames ? "work area name" : "labor classification"}
          {workAreas.length === 1 ? "" : "s"} {workAreas.length === 1 ? "was" : "were"} underpaid for this employee,
          project{hasWorkAreaNames ? ", pay period and labor classification." : " and pay period."}
        </Typography>
        <Link
          component="button"
          type="button"
          className="UnderPaymentDetail-expandAll"
          onClick={handleExpandAllToggle}
        >
          {allExpanded ? "Collapse all calculations" : "Expand all calculations"}
        </Link>
      </div>

      <div className="UnderPaymentDetail-workAreas">
        {workAreas.map((workArea, index) => (
          <WorkAreaAccordion
            key={workArea.id}
            index={index + 1}
            workArea={workArea}
            expanded={expandedIds.includes(workArea.id)}
            onToggle={() => handleToggleAccordion(workArea.id)}
          />
        ))}
      </div>
    </div>
  );
}

UnderPaymentDetail.propTypes = {
  record: PropTypes.shape({
    employeeName: PropTypes.string,
    weekEnding: PropTypes.string,
    project: PropTypes.string,
    laborClassification: PropTypes.string,
    grossAmount: PropTypes.number,
    totalBackPayment: PropTypes.number,
    workAreas: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        st: PropTypes.number,
        ot: PropTypes.number,
        dt: PropTypes.number,
        backPayment: PropTypes.number
      })
    )
  }),
  onBackToList: PropTypes.func.isRequired
};

UnderPaymentDetail.defaultProps = {
  record: {
    employeeName: "",
    weekEnding: "",
    project: "",
    laborClassification: "",
    grossAmount: 0,
    totalBackPayment: 0,
    workAreas: []
  }
};